import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Space,
  message,
  Card,
  Tag,
  Popconfirm,
  Switch,
  Tooltip,
} from "antd";
import {
  Plus,
  RefreshCw,
  Edit,
  Trash2,
  Play,
  FileText,
  Clock,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import apiService from "../../services/api";
import {
  setTasks,
  addTask,
  updateTask,
  removeTask,
  setLoading,
  setError,
  selectTasks,
  selectTaskLoading,
} from "../../store/taskSlice";
import TaskForm from "./TaskForm";
import TaskLogs from "./TaskLogs";
import { formatDateTime } from "../../utils/formatters";

const TaskList = () => {
  const dispatch = useDispatch();
  const tasks = useSelector(selectTasks);
  const loading = useSelector(selectTaskLoading);
  const [showModal, setShowModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const loadTasks = useCallback(async () => {
    dispatch(setLoading(true));
    try {
      const params = {
        page: pagination.current,
        page_size: pagination.pageSize,
      };

      const response = await apiService.tasks.getTasks(params);
      if (!response.data.success) {
        throw new Error(response.data.message || "获取任务列表失败");
      }

      dispatch(setTasks(response.data.data?.list || []));
      setPagination((prev) => ({
        ...prev,
        total: response.data.data?.total || 0,
      }));
    } catch (err) {
      message.error(err.message || "获取任务列表失败");
      dispatch(setError(err.message));
    }
  }, [dispatch, pagination.current, pagination.pageSize]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const handleCreate = () => {
    setCurrentTask(null);
    setShowModal(true);
  };

  const handleEdit = (record) => {
    setCurrentTask(record);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await apiService.tasks.deleteTask(id);
      if (!response.data.success) {
        throw new Error(response.data.message || "删除失败");
      }
      message.success("删除成功");
      dispatch(removeTask(id));
    } catch (err) {
      message.error(err.message || "删除失败");
    }
  };

  const handleToggle = async (id, enabled) => {
    try {
      const response = await apiService.tasks.toggleTask(id);
      if (!response.data.success) {
        throw new Error(response.data.message || "切换状态失败");
      }
      message.success(enabled ? "已禁用" : "已启用");
      dispatch(updateTask(response.data.data));
    } catch (err) {
      message.error(err.message || "切换状态失败");
    }
  };

  const handleRun = async (id) => {
    try {
      const response = await apiService.tasks.runTask(id);
      if (!response.data.success) {
        throw new Error(response.data.message || "执行失败");
      }
      message.success("任务已开始执行");
      loadTasks();
    } catch (err) {
      message.error(err.message || "执行失败");
    }
  };

  const handleViewLogs = (record) => {
    setCurrentTask(record);
    setShowLogsModal(true);
  };

  const handleFormSubmit = async (values) => {
    try {
      if (currentTask) {
        const response = await apiService.tasks.updateTask(currentTask.id, values);
        if (!response.data.success) {
          throw new Error(response.data.message || "更新失败");
        }
        message.success("更新成功");
        dispatch(updateTask(response.data.data));
      } else {
        const response = await apiService.tasks.createTask(values);
        if (!response.data.success) {
          throw new Error(response.data.message || "创建失败");
        }
        message.success("创建成功");
        dispatch(addTask(response.data.data));
      }
      setShowModal(false);
      loadTasks();
    } catch (err) {
      message.error(err.message || "操作失败");
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      report: "报告生成",
      validation: "数据校验",
      sync: "数据同步",
      backup: "数据备份",
      cleanup: "数据清理",
      alert_check: "告警检查",
    };
    return labels[type] || type;
  };

  const getStatusTag = (status) => {
    const colors = {
      idle: "default",
      running: "processing",
      success: "success",
      failed: "error",
    };
    const labels = {
      idle: "空闲",
      running: "运行中",
      success: "成功",
      failed: "失败",
    };
    return <Tag color={colors[status]}>{labels[status]}</Tag>;
  };

  const columns = [
    {
      title: "任务名称",
      dataIndex: "name",
      key: "name",
      width: 200,
    },
    {
      title: "类型",
      dataIndex: "type",
      key: "type",
      width: 120,
      render: (type) => getTypeLabel(type),
    },
    {
      title: "调度规则",
      dataIndex: "schedule",
      key: "schedule",
      width: 150,
    },
    {
      title: "状态",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => getStatusTag(status),
    },
    {
      title: "上次运行",
      dataIndex: "last_run_at",
      key: "last_run_at",
      width: 180,
      render: (text) => (text ? formatDateTime(text) : "-"),
    },
    {
      title: "下次运行",
      dataIndex: "next_run_at",
      key: "next_run_at",
      width: 180,
      render: (text) => (text ? formatDateTime(text) : "-"),
    },
    {
      title: "启用",
      dataIndex: "enabled",
      key: "enabled",
      width: 80,
      render: (enabled, record) => (
        <Switch
          checked={enabled}
          onChange={() => handleToggle(record.id, enabled)}
          checkedChildren="是"
          unCheckedChildren="否"
        />
      ),
    },
    {
      title: "操作",
      key: "action",
      width: 200,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="立即执行">
            <Button
              type="link"
              size="small"
              icon={<Play size={16} />}
              onClick={() => handleRun(record.id)}
              disabled={record.status === "running"}
            />
          </Tooltip>
          <Tooltip title="查看日志">
            <Button
              type="link"
              size="small"
              icon={<FileText size={16} />}
              onClick={() => handleViewLogs(record)}
            />
          </Tooltip>
          <Tooltip title="编辑">
            <Button
              type="link"
              size="small"
              icon={<Edit size={16} />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定要删除这个任务吗?"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button
                type="link"
                size="small"
                danger
                icon={<Trash2 size={16} />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Button
              type="primary"
              icon={<Plus size={16} />}
              onClick={handleCreate}
            >
              新建任务
            </Button>
            <Button icon={<RefreshCw size={16} />} onClick={loadTasks}>
              刷新
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={tasks}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => {
              setPagination((prev) => ({ ...prev, current: page, pageSize }));
            },
          }}
          scroll={{ x: 1400 }}
        />
      </Card>

      <Modal
        title={currentTask ? "编辑任务" : "新建任务"}
        open={showModal}
        onCancel={() => setShowModal(false)}
        footer={null}
        width={600}
        destroyOnClose
      >
        <TaskForm
          initialValues={currentTask}
          onSubmit={handleFormSubmit}
          onCancel={() => setShowModal(false)}
        />
      </Modal>

      <Modal
        title="任务执行日志"
        open={showLogsModal}
        onCancel={() => setShowLogsModal(false)}
        footer={null}
        width={900}
        destroyOnClose
      >
        <TaskLogs taskId={currentTask?.id} />
      </Modal>
    </div>
  );
};

export default TaskList;
